// Pure rules for the Users screen's bulk upload, Register / Register all, and
// Add to all companies (backend `_specs/bulk-user-upload-spec.md` §4, §5).
//
// The server decides who is added and how (`import-users` classifies every row,
// on the dry run and again on the real import). These only turn a file into the
// rows the server is sent, and read back what it said. Nothing here writes.

import { isPrimaryCompany } from '../tenancy/families';
import { currentCompany } from '../tenancy/companies';

export const MAX_UPLOAD_ROWS = 500;
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
// The backend's Joi limits (spec §4). A longer email cannot be a real address,
// so it is reported here rather than letting it 400 the whole request.
export const MAX_NAME_LENGTH = 100;
export const MAX_EMAIL_LENGTH = 254;

const SUPPORT = 'Support';
const OWNER = 'Owner';

/** Lower-case, spaces / underscores / hyphens dropped: "Full_Name" → "fullname". */
export const normalizeKey = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

/** A role spelled the way the server compares them: "C Level" = "C_Level" = "c-level". */
export const roleKey = normalizeKey;

/** How a role reads on screen: the company stores `C_Level`. */
export const roleLabel = (role) => String(role ?? '').replace(/_/g, ' ');

// Which heading names which column. `company` is optional.
const HEADER_ALIASES = {
  name: ['name', 'fullname'],
  email: ['email', 'emailaddress'],
  role: ['role'],
  company: ['company']
};
const REQUIRED_COLUMNS = [
  ['name', 'Name'],
  ['email', 'Email'],
  ['role', 'Role']
];

const cellText = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
};

const isBlankRow = (cells) => !Array.isArray(cells) || cells.every((c) => cellText(c) === '');

/** `{name, email, role, company}` → column index (company may be absent). */
export function matchHeaders(headerCells) {
  const columns = {};
  (headerCells || []).forEach((cell, index) => {
    const key = normalizeKey(cellText(cell));
    Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
      if (columns[field] === undefined && aliases.includes(key)) columns[field] = index;
    });
  });
  return columns;
}

/**
 * The upload's rows out of a sheet (an array of row arrays, as papaparse and
 * read-excel-file both give it). The first non-blank row is the heading row.
 *
 * `line` is the row's POSITION among the people in the file: 1 is the first
 * person under the headings, blank rows not counted. It is what is sent to the
 * server as `row` and what the preview's "#" column shows. Deliberately not a
 * spreadsheet row number: read-excel-file starts at the sheet's used range and
 * a CSV cell may span lines, so a "row 12" could be wrong; a position cannot.
 *
 * Answers `{rows, hasCompany}` or `{error}`.
 */
export function extractRows(matrix) {
  const lines = Array.isArray(matrix) ? matrix : [];
  const headerIndex = lines.findIndex((cells) => !isBlankRow(cells));
  if (headerIndex === -1) return { error: 'The file is empty.' };

  const columns = matchHeaders(lines[headerIndex]);
  const missing = REQUIRED_COLUMNS.filter(([field]) => columns[field] === undefined).map(
    ([, heading]) => heading
  );
  if (missing.length) {
    return {
      error: `The file has no ${missing.join(', ')} column${
        missing.length === 1 ? '' : 's'
      }. The first row must hold the headings Name, Email and Role (and optionally Company).`
    };
  }

  const hasCompany = columns.company !== undefined;
  const rows = [];
  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const cells = lines[i];
    if (isBlankRow(cells)) continue;
    rows.push({
      line: rows.length + 1,
      name: cellText(cells[columns.name]),
      email: cellText(cells[columns.email]),
      role: cellText(cells[columns.role]),
      ...(hasCompany ? { company: cellText(cells[columns.company]) } : {})
    });
  }
  if (!rows.length) return { error: 'The file has headings but no rows.' };
  // With a Company column only the ticked companies go on, so the cap is
  // applied to those (`tooManyRows`), not to the whole file.
  if (!hasCompany && rows.length > MAX_UPLOAD_ROWS) {
    return { error: tooManyRowsMessage(rows.length) };
  }
  return { rows, hasCompany };
}

export const tooManyRowsMessage = (count) =>
  `${count} rows — at most ${MAX_UPLOAD_ROWS} can be uploaded at once. Split the file.`;

export const tooManyRows = (rows) => (rows?.length ?? 0) > MAX_UPLOAD_ROWS;

/** Whether the file can be read at all: its type and size. Null when fine. */
export function fileProblem(file) {
  if (!file) return 'Choose a file.';
  const name = String(file.name || '').toLowerCase();
  if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
    return 'Upload a .csv or .xlsx file.';
  }
  if (file.size > MAX_UPLOAD_BYTES) return 'The file is larger than 2 MB.';
  return null;
}

export const isXlsxFile = (file) =>
  String(file?.name || '')
    .toLowerCase()
    .endsWith('.xlsx');

/**
 * The distinct Company values, in the order the file first uses them, with how
 * many rows each holds. Grouped case-insensitively; a blank cell is its own value.
 */
export function companyValues(rows) {
  const byKey = new Map();
  (rows || []).forEach((row) => {
    const value = String(row?.company ?? '').trim();
    const key = value.toLowerCase();
    const entry = byKey.get(key);
    if (entry) entry.count += 1;
    else byKey.set(key, { key, value, count: 1 });
  });
  return [...byKey.values()];
}

/** Only the rows whose Company value is ticked (`selectedKeys` from `companyValues`). */
export function filterRowsByCompany(rows, selectedKeys) {
  const selected = new Set(selectedKeys || []);
  return (rows || []).filter((row) =>
    selected.has(
      String(row?.company ?? '')
        .trim()
        .toLowerCase()
    )
  );
}

// Roles an upload can never give (spec §3: `resolveAssignableRole` refuses them).
const NOT_ASSIGNABLE = ['owner', 'admin', 'vendor', 'subadmin', 'operator', 'apperson'];

// What the server offers a company that has no roles stored
// (roleAssignmentRules.DEFAULT_COMPANY_ROLES).
const DEFAULT_COMPANY_ROLES = ['Owner', 'Support', 'Accountant', 'C_Level', 'Manager'];

/** The company's roles an upload can give, de-duplicated, in the company's spelling. */
export function assignableRoles(companyRoles) {
  const seen = new Set();
  const stored = (companyRoles || []).filter((role) => typeof role === 'string');
  return (stored.length ? stored : DEFAULT_COMPANY_ROLES).filter((role) => {
    const key = roleKey(role);
    if (!key || NOT_ASSIGNABLE.includes(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** The company's spelling of a role value, or null when it is not one the upload can give. */
export function matchRole(value, roles) {
  const key = roleKey(value);
  if (!key) return null;
  return (roles || []).find((role) => roleKey(role) === key) ?? null;
}

/**
 * Role values in the file that are not one of the company's assignable roles,
 * each once (by role key), with its row count. A blank role is one of them.
 * Each needs a role picked before the upload can go on.
 */
export function unmatchedRoles(rows, roles) {
  const byKey = new Map();
  (rows || []).forEach((row) => {
    if (matchRole(row?.role, roles)) return;
    const key = roleKey(row?.role);
    const entry = byKey.get(key);
    if (entry) entry.count += 1;
    else byKey.set(key, { key, value: String(row?.role ?? '').trim(), count: 1 });
  });
  return [...byKey.values()];
}

/** Every unmatched value has a role picked. */
export const allRolesMapped = (unmatched, mapping) =>
  (unmatched || []).every((entry) => Boolean(mapping?.[entry.key]));

/** The rows with each role in the company's spelling: a match, else the picked one. */
export function applyRoleMapping(rows, roles, mapping) {
  return (rows || []).map((row) => ({
    ...row,
    role: matchRole(row?.role, roles) ?? mapping?.[roleKey(row?.role)] ?? row?.role
  }));
}

/**
 * The `import-users` rows, and the ones that cannot be sent at all (an email
 * longer than the server accepts), reported back as errors in the server's shape.
 */
export function buildImportRows(rows) {
  const send = [];
  const local = [];
  (rows || []).forEach((row) => {
    if (row.email.length > MAX_EMAIL_LENGTH) {
      local.push({
        row: row.line,
        email: row.email,
        name: row.name,
        role: row.role,
        outcome: 'error',
        reason: 'invalidEmail'
      });
      return;
    }
    send.push({
      row: row.line,
      name: row.name.slice(0, MAX_NAME_LENGTH),
      email: row.email,
      role: row.role
    });
  });
  return { send, local };
}

export const OUTCOMES = [
  'add',
  'hasLogin',
  'alreadyUser',
  'alreadyInvited',
  'addedInPrimary',
  'error'
];

export const OUTCOME_LABELS = {
  add: 'Add',
  hasLogin: 'Has a login',
  alreadyUser: 'Already a user',
  alreadyInvited: 'Already invited',
  addedInPrimary: 'In primary',
  error: 'Error'
};

const OUTCOME_TEXT = {
  add: 'Will be added',
  hasLogin: 'Has a login in another company — will get an invite',
  alreadyUser: 'Already a user in this company',
  alreadyInvited: 'Already invited to this company',
  addedInPrimary: 'Added in the primary company — arrives here automatically'
};

const REASON_TEXT = {
  invalidEmail: 'Not a valid email address',
  missingName: 'The name is blank',
  duplicateInFile: 'The same email is on an earlier row',
  roleNotAssignable: 'This role cannot be given by upload',
  roleNotACompanyRole: "Not one of this company's roles",
  isOwner: "This is the company's Owner",
  loginInOtherCompany:
    'Has a login in another company, and this company is not set up for several companies'
};

/** Reasons whose wording needs something from the row itself. */
const REASON_FROM_ROW = {
  useMainCompany: (result) =>
    `Accountants and sub-admins are added in ${
      result?.primaryName || 'the main company'
    }, and reach this company from there. Upload them there.`,
  loginCannotJoin: (result) =>
    `Has a login that cannot join this company${result?.message ? `: ${result.message}` : ''}`
};

/** What a preview row means, in words. */
export function describeResult(result) {
  const base =
    result?.outcome === 'error'
      ? REASON_FROM_ROW[result?.reason]?.(result) ||
        REASON_TEXT[result?.reason] ||
        result?.reason ||
        'Cannot be added'
      : OUTCOME_TEXT[result?.outcome] || result?.outcome || '';
  return result?.existingRole ? `${base} (currently ${roleLabel(result.existingRole)})` : base;
}

/** Errors first, then by line. */
export function sortResults(results) {
  return [...(results || [])].sort((a, b) => {
    const ae = a?.outcome === 'error' ? 0 : 1;
    const be = b?.outcome === 'error' ? 0 : 1;
    if (ae !== be) return ae - be;
    return (Number(a?.row) || 0) - (Number(b?.row) || 0);
  });
}

/** The server's counts, plus any rows refused here before sending. */
export function mergeCounts(counts, localErrors) {
  const merged = {};
  OUTCOMES.forEach((outcome) => {
    merged[outcome] = Number(counts?.[outcome]) || 0;
  });
  merged.error += (localErrors || []).length;
  return merged;
}

/** How many the Import button adds: new users plus invites to people with a login. */
export const importableCount = (counts) =>
  (Number(counts?.add) || 0) + (Number(counts?.hasLogin) || 0);

/** The emails Register all now should register after an import. */
export const importedEmails = (results) =>
  (results || [])
    .filter((r) => r?.outcome === 'add' || r?.outcome === 'hasLogin')
    .map((r) => r.email);

/** `{results, counts}` out of an import-users response, or null. */
export function readImportResult(response) {
  if (response?.status !== 200) return null;
  const data = response?.data?.payload?.data;
  if (!data || !Array.isArray(data.results)) return null;
  return { results: data.results, counts: data.counts || {} };
}

// ---- The Users grid ------------------------------------------------------

/** Signed up, read off either shape: the API's `signUp` or the grid's `isSignUp`. */
export const isRowSignedUp = (row) => row?.signUp === true || row?.isSignUp === 'Completed';

/**
 * An uploaded row nobody has registered yet: Register sends its first email.
 * ERP employee rows keep their own Register.
 */
export const needsImportRegister = (row) =>
  Boolean(row?.importedAt) &&
  !isRowSignedUp(row) &&
  !row?.invitedAt &&
  !row?.erpUser &&
  !row?.familyMember;

/**
 * Which send button a pending row gets, if any:
 * - `register`: an uploaded row's FIRST email (register-imported), Support only;
 * - `resendInvite`: re-send to an uploaded person who has a login elsewhere —
 *   their email is a company INVITE, which only register-imported sends
 *   (resend-invite would send a sign-up link they cannot use), Support only;
 * - `resend`: today's resend-invite (a sign-up link);
 * - `null`: no button. That is an uploaded row someone other than Support is
 *   looking at before Support has registered it (a Resend there would send a
 *   first email the upload has not, and leave it in Register all's count), and
 *   any signed-up or ERP row.
 */
export function pendingRowSend(row, mayRegister) {
  // The main company's people are managed there, not in this company.
  if (isRowSignedUp(row) || row?.erpUser || row?.familyMember) return null;
  if (!row?.importedAt) return 'resend';
  if (!row?.invitedAt) return mayRegister ? 'register' : null;
  if (row?.hasLogin) return mayRegister ? 'resendInvite' : null;
  return 'resend';
}

/** "Nov 3, 2026", or '' for a value that is not a date. */
export function formatSentDate(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * What the Sign Up column says about a row, and the tooltip under it:
 * - `done`: signed up;
 * - `sent`: the sign-up email (or, for someone with a login, the company
 *   invite) went out — `invitedAt`, stamped by the server when it is sent;
 * - `notSent`: uploaded, and Register has not sent it yet;
 * - `warning`: one of the main company's people who registered there but could
 *   not be added to this company;
 * - `pending`: an older pending row, sent before the send time was recorded.
 * A row from the main company (`familyMember`) also says they join this
 * company when they register there.
 */
export function signUpStatus(row, { mayRegister = true } = {}) {
  const family = row?.familyMember;
  // Advice names a button only when this viewer has it (pendingRowSend).
  const hasSend = !family && pendingRowSend(row, mayRegister) !== null;
  if (isRowSignedUp(row) && !family) return { tone: 'done', label: 'Completed', detail: '' };
  const primary = family?.primaryName || 'the main company';
  if (family?.status === 'neverSynced') {
    return {
      tone: 'warning',
      label: 'Not added',
      detail: `Registered in ${primary} before its companies were linked. Use Add to all companies in ${primary} to add them here.`
    };
  }
  if (family?.status === 'notAdded') {
    return {
      tone: 'warning',
      label: 'Not added',
      detail: family.reason
        ? `Registered in ${primary}, but could not be added to this company: ${family.reason}`
        : `Registered in ${primary}, but not added to this company yet. Use Add to all companies in ${primary}.`
    };
  }
  const joins = family ? ` They are added to this company when they register in ${primary}.` : '';
  // The server clears inviteFailedAt when a later send works, so a value here
  // is the latest attempt.
  if (row?.inviteFailedAt) {
    const on = formatSentDate(row.inviteFailedAt);
    const retry = family
      ? `Send it again from ${primary}.`
      : !hasSend
      ? 'Ask Support to send it again.'
      : row?.importedAt && !row?.invitedAt
      ? 'Press Register to try again.'
      : 'Press Resend to try again.';
    return {
      tone: 'failed',
      label: 'Email not sent',
      detail: `The sign-up email could not be sent${on ? ` on ${on}` : ''}. ${retry}${joins}`
    };
  }
  if (row?.invitedAt) {
    const on = formatSentDate(row.invitedAt);
    const what = row?.hasLogin ? 'Company invite' : 'Sign-up email';
    return {
      tone: 'sent',
      label: row?.hasLogin ? 'Invite sent' : 'Email sent',
      detail: `${what} sent${on ? ` on ${on}` : ''}. Waiting for them to ${
        row?.hasLogin ? 'accept it' : 'register'
      }.${joins}`
    };
  }
  if (family) {
    return {
      tone: 'notSent',
      label: 'Not sent',
      detail: `No sign-up email has been sent yet. Register them in ${primary}.${joins}`
    };
  }
  if (row?.importedAt) {
    return {
      tone: 'notSent',
      label: 'Not sent',
      detail: hasSend
        ? 'Uploaded. No email has been sent yet: press Register to send the sign-up email.'
        : 'Uploaded. No email has been sent yet: Support sends it with Register.'
    };
  }
  return { tone: 'pending', label: 'Pending', detail: 'Waiting for them to sign up.' };
}

/**
 * The main company's Accountants and Sub Admins an attached company is waiting
 * for (`GET /api/v1/user/family-pending`), as Users-grid rows. Shown, never
 * acted on here: they are registered and removed in the main company.
 */
export function readFamilyPending(response) {
  if (response?.status !== 200) return [];
  const data = response?.data?.payload?.data;
  const people = Array.isArray(data?.people) ? data.people : [];
  const primaryName = String(data?.primaryName ?? '');
  return people
    .filter((person) => person?.email)
    .map((person) => ({
      email: person.email,
      name: person.name || '',
      role: person.role,
      subAdmin: person.subAdmin === true,
      signUp: false,
      erpUser: false,
      invitedAt: person.invitedAt || null,
      inviteFailedAt: person.inviteFailedAt || null,
      hasLogin: person.hasLogin === true,
      familyMember: { primaryName, status: person.status, reason: person.reason || '' }
    }));
}

/**
 * Sorting the Sign Up column: what needs doing first (a failed send, someone
 * not added, an email not sent yet), then waiting, then done.
 */
export const SIGN_UP_SORT_ORDER = [
  'Email not sent',
  'Not added',
  'Not sent',
  'Pending',
  'Invite sent',
  'Email sent',
  'Completed'
];
export function compareSignUpLabels(a, b) {
  const rank = (label) => {
    const at = SIGN_UP_SORT_ORDER.indexOf(label);
    return at === -1 ? SIGN_UP_SORT_ORDER.length : at;
  };
  return rank(a) - rank(b);
}

/**
 * Whether the Users screen asks for the main company's people: an Owner or
 * Support, in an ATTACHED company of a switched-on family. The server answers
 * an empty list to anyone else; this saves asking.
 */
export function asksFamilyPending(role, myCompanies, companyId) {
  if (role !== OWNER && role !== SUPPORT) return false;
  if (myCompanies?.multiCompany !== true) return false;
  return currentCompany(myCompanies, companyId)?.isPrimary === false;
}

/**
 * The toast for an Add User answer: a 200 whose metadata says ACCEPTED added
 * the user but could not send the sign-up email, which is a warning.
 */
export const addUserResultTone = (result) =>
  result?.data?.metadata?.status === 'ACCEPTED' ? 'warning' : 'success';

/** The emails Register all registers on the Users screen. */
export const registerAllEmails = (users) =>
  (users || [])
    .filter(needsImportRegister)
    .map((u) => u.email)
    .filter(Boolean);

/** The companies a family sync could not add this person to. */
export const familySyncRefused = (row) =>
  Array.isArray(row?.familySync?.refused) ? row.familySync.refused : [];

/** Tooltip text for a refused family sync: "Company: message" per line. */
export const familySyncRefusedText = (row) =>
  familySyncRefused(row)
    .map((r) => `${r?.companyName || r?.companyId}: ${r?.message || r?.reason || 'Not added'}`)
    .join('\n');

/** Upload users and Register / Register all: Support only (spec §4). */
export const canUploadUsers = (role) => role === SUPPORT;

/** Add to all companies: Owner or Support, on the primary of a switched-on family. */
export function canAddToAllCompanies(role, myCompanies, companyId) {
  if (role !== OWNER && role !== SUPPORT) return false;
  if (myCompanies?.multiCompany !== true) return false;
  return isPrimaryCompany(myCompanies, companyId);
}

/** Register / Register all results, in one sentence. */
export function summarizeRegister(response) {
  const results = response?.data?.payload?.data?.results;
  if (!Array.isArray(results)) return { ok: false, message: null };
  const count = (outcome) => results.filter((r) => r?.outcome === outcome).length;
  const parts = [];
  const sent = count('sent');
  const invited = count('invited');
  const signedUp = count('alreadySignedUp');
  const notFound = count('notFound');
  const refused = count('refused');
  // A later batch failed: these were never sent (registerImportedUsers).
  const notSent = results.filter((r) => r?.outcome === 'failed' && r?.reason === 'notSent').length;
  const failed = count('failed') - notSent;
  if (sent) parts.push(`${sent} registration email${sent === 1 ? '' : 's'} sent`);
  if (invited) parts.push(`${invited} invite${invited === 1 ? '' : 's'} sent`);
  if (signedUp) parts.push(`${signedUp} already signed up`);
  if (notFound) parts.push(`${notFound} not found`);
  if (refused) parts.push(`${refused} refused`);
  if (failed) parts.push(`${failed} failed`);
  if (notSent) parts.push(`${notSent} not sent — try Register all again`);
  return {
    ok:
      notSent === 0 &&
      (sent + invited > 0 || (refused + failed + notFound === 0 && results.length > 0)),
    message: parts.length ? `${parts.join(', ')}.` : 'Nothing to register.'
  };
}

// Why a family sync did nothing for one person (familyMembership `skipped`).
const FAMILY_SKIP_TEXT = {
  familyNotOn: 'Multiple companies is not switched on for this group',
  notFamilyWide: 'Only signed-up Accountants and sub-admins are added to every company',
  noRecord: 'They have not signed up in this company yet'
};

/** A family-sync answer (one person or everyone), in one sentence. */
export function summarizeFamilySync(response) {
  const data = response?.data?.payload?.data;
  if (!data || typeof data !== 'object') return { ok: false, message: null };
  // One person, and nothing was done: say why, and not as a success.
  if (!Array.isArray(data.people) && data.skipped) {
    return {
      ok: false,
      message: `${FAMILY_SKIP_TEXT[data.skipped] || 'Nothing was done for this person'}.`
    };
  }
  const people = Array.isArray(data.people) ? data.people : [data];
  let added = 0;
  let refused = 0;
  let skipped = 0;
  people.forEach((person) => {
    added += Array.isArray(person?.added) ? person.added.length : 0;
    refused += Array.isArray(person?.refused) ? person.refused.length : 0;
    if (person?.skipped) skipped += 1;
  });
  const parts = [];
  parts.push(`Added to ${added} compan${added === 1 ? 'y' : 'ies'}`);
  if (refused) parts.push(`${refused} refused`);
  if (Array.isArray(data.people)) {
    parts.push(`${people.length} ${people.length === 1 ? 'person' : 'people'} checked`);
    if (skipped) parts.push(`${skipped} skipped`);
  }
  return { ok: refused === 0, message: `${parts.join(', ')}.` };
}
